import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { Project } from 'src/app/core/objects/project';
import { CargoSpace, Group, WorkSpace } from 'src/app/core/types';
import {
  cargoSpaceHasPalletLayers,
  getPalletLayerGroupsFromCargoSpace,
  listGroupsExcludingPalletLayers,
} from './pallet-loading-space.helper';
import { palletSpaceQuantity } from './pallet-workspace-collapse.helper';

export const PALLET_OVERFLOW_POPUP_DISMISSED_PREFIX = 'palletOverflowPopupDismissed:';

export function countPalletWorkSpaces(project: Project | undefined | null): number {
  return countWorkSpacesOfType(project, LoadingSpaceType.Pallet);
}

export function countWorkSpacesOfType(
  project: Project | undefined | null,
  type: LoadingSpaceType,
): number {
  return (project?.workSpaces ?? []).filter((workSpace) => workSpaceType(workSpace) === type).length;
}

export function countLoadingSpacesOfType(
  project: Project | undefined | null,
  type: LoadingSpaceType,
): number {
  return (project?.workSpaces ?? []).reduce((sum, workSpace) => {
    if (workSpaceType(workSpace) !== type) {
      return sum;
    }
    if (type === LoadingSpaceType.Pallet) {
      return sum + palletSpaceQuantity(workSpace);
    }
    return sum + 1;
  }, 0);
}

export function overflowingLoadingSpaceType(
  project: Project | undefined | null,
): LoadingSpaceType | undefined {
  const types = new Set<LoadingSpaceType>();
  for (const workSpace of project?.workSpaces ?? []) {
    if (leftoverOnWorkSpace(workSpace) <= 0) {
      continue;
    }
    const type = workSpaceType(workSpace);
    if (type == null) {
      return undefined;
    }
    types.add(type);
  }
  if (types.size !== 1) {
    return undefined;
  }
  return types.values().next().value;
}

export function overflowingWorkSpaceId(project: Project | undefined | null): number | undefined {
  const type = overflowingLoadingSpaceType(project);
  if (type == null) {
    return undefined;
  }
  let chosen: WorkSpace | undefined;
  let bestUnplaced = -1;
  let bestOutside = -1;
  for (const workSpace of project?.workSpaces ?? []) {
    if (
      workSpaceType(workSpace) !== type ||
      leftoverOnWorkSpace(workSpace) <= 0 ||
      (workSpace.stops?.length ?? 0) > 0
    ) {
      continue;
    }
    const unplaced = unplacedOnWorkSpace(workSpace);
    const outside = groupQuantity(workSpace.outsideSpace?.groups);
    // Unplaced layers mark the pallet that was just added to. On a tie, the later
    // pallet wins so a stale leftover on an earlier pallet is not the target.
    if (
      !chosen ||
      unplaced > bestUnplaced ||
      (unplaced === bestUnplaced && outside >= bestOutside)
    ) {
      chosen = workSpace;
      bestUnplaced = unplaced;
      bestOutside = outside;
    }
  }
  return chosen?.id;
}

export function shouldOfferPalletCountRecommendation(project: Project | undefined | null): boolean {
  if (!project?.workSpaces?.length) {
    return false;
  }
  const type = overflowingLoadingSpaceType(project);
  if (type !== LoadingSpaceType.Pallet) {
    return false;
  }
  const candidates = (project.workSpaces ?? []).filter(
    (workSpace) =>
      workSpaceType(workSpace) === type &&
      leftoverOnWorkSpace(workSpace) > 0 &&
      (workSpace.stops?.length ?? 0) === 0,
  );
  if (!candidates.length) {
    return false;
  }
  const palletTypes = new Set(candidates.map(palletTypeKey));
  if (palletTypes.size !== 1) {
    return false;
  }
  return (
    candidates.some((workSpace) => workSpaceHasPalletLayers(workSpace)) ||
    hasSingleLoadGroupAcross(candidates)
  );
}

export function leftoverLoadsExceedSpaceDimensions(
  project: Project | undefined | null,
): boolean {
  const type = overflowingLoadingSpaceType(project);
  if (type !== LoadingSpaceType.Pallet) {
    return false;
  }
  let sawUnfittable = false;
  for (const workSpace of project?.workSpaces ?? []) {
    if (workSpaceType(workSpace) !== type || leftoverOnWorkSpace(workSpace) <= 0) {
      continue;
    }
    const space = workSpace.cargoSpaces?.[0]?.dimensions;
    for (const leftover of workSpace.outsideSpace?.groups ?? []) {
      if ((leftover.quantity ?? 0) <= 0) {
        continue;
      }
      if (!leftover.dimensions || !space) {
        return false;
      }
      if (loadFitsSpace(leftover, space)) {
        return false;
      }
      sawUnfittable = true;
    }
  }
  return sawUnfittable;
}

export function palletOverflowSignature(project: Project | undefined | null): string {
  const type = overflowingLoadingSpaceType(project);
  const leftover = leftoverLoadQuantity(project);
  const spaceCount = type == null ? 0 : countWorkSpacesOfType(project, type);
  return `${project?.id ?? 'none'}:${type ?? 'none'}:${spaceCount}:${leftover}`;
}

export function overflowPopupCopyKey(type: LoadingSpaceType | undefined): string {
  switch (type) {
    case LoadingSpaceType.Vehicle:
      return 'vehicle_wont_fit_single_popup';
    case LoadingSpaceType.SeaContainer:
      return 'container_wont_fit_single_popup';
    case LoadingSpaceType.AirContainer:
      return 'air_container_wont_fit_single_popup';
    default:
      return 'pallet_wont_fit_single_popup';
  }
}

export function overflowAddedNotificationKey(type: LoadingSpaceType | undefined): string {
  switch (type) {
    case LoadingSpaceType.Vehicle:
      return 'notification_vehicles_added';
    case LoadingSpaceType.SeaContainer:
      return 'notification_containers_added';
    case LoadingSpaceType.AirContainer:
      return 'notification_air_containers_added';
    default:
      return 'notification_pallets_added';
  }
}

export const PALLET_OVERFLOW_PROJECT_INSTANCE_KEY = 'palletOverflowProjectInstance';

export function rotatePalletOverflowProjectInstance(): string {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(PALLET_OVERFLOW_PROJECT_INSTANCE_KEY, id);
  return id;
}

export function isPalletOverflowPopupDismissed(): boolean {
  return localStorage.getItem(dismissKey()) === 'true';
}

export function dismissPalletOverflowPopup(): void {
  localStorage.setItem(dismissKey(), 'true');
}

function currentProjectInstanceId(): string {
  const existing = localStorage.getItem(PALLET_OVERFLOW_PROJECT_INSTANCE_KEY);
  if (existing) {
    return existing;
  }
  return rotatePalletOverflowProjectInstance();
}

function dismissKey(): string {
  return `${PALLET_OVERFLOW_POPUP_DISMISSED_PREFIX}${currentProjectInstanceId()}`;
}

function workSpaceType(workSpace: WorkSpace | undefined): LoadingSpaceType | undefined {
  const cargoSpaces = workSpace?.cargoSpaces ?? [];
  if (!cargoSpaces.length) {
    return undefined;
  }
  const type = cargoSpaces[0]?.type;
  if (type == null || cargoSpaces.some((cargoSpace) => cargoSpace?.type !== type)) {
    return undefined;
  }
  return type;
}

function leftoverOnWorkSpace(workSpace: WorkSpace | undefined): number {
  return groupQuantity(workSpace?.outsideSpace?.groups) + unplacedOnWorkSpace(workSpace);
}

function unplacedOnWorkSpace(workSpace: WorkSpace | undefined): number {
  return (workSpace?.cargoSpaces ?? []).reduce(
    (sum, cargoSpace) => sum + unplacedLayerQuantity(cargoSpace),
    0,
  );
}

function leftoverLoadQuantity(project: Project | undefined | null): number {
  const type = overflowingLoadingSpaceType(project);
  if (type == null) {
    return 0;
  }
  return (project?.workSpaces ?? [])
    .filter((workSpace) => workSpaceType(workSpace) === type)
    .reduce((sum, workSpace) => sum + leftoverOnWorkSpace(workSpace), 0);
}

function unplacedLayerQuantity(cargoSpace: CargoSpace): number {
  let leftover = 0;
  for (const palletGroup of getPalletLayerGroupsFromCargoSpace(cargoSpace)) {
    for (const layer of palletGroup.layers ?? []) {
      leftover += unplacedLayerStackCount(layer.layersLoaded);
    }
  }
  return leftover;
}

/**
 * `layer.quantity` is the number of loads on one layer, not how many layers were requested.
 * Unplaced layers are `layersLoaded` entries the API left without a position.
 */
function unplacedLayerStackCount(layersLoaded: unknown[] | undefined): number {
  if (!Array.isArray(layersLoaded)) {
    return 0;
  }
  return layersLoaded.reduce<number>((sum, entry) => {
    const placed = entry as { position?: unknown; numberOfStackingLayers?: number };
    if (placed?.position) {
      return sum;
    }
    return sum + Math.max(0, placed?.numberOfStackingLayers ?? 0);
  }, 0);
}

function workSpaceHasPalletLayers(workSpace: WorkSpace | undefined): boolean {
  return (workSpace?.cargoSpaces ?? []).some((cargoSpace) => cargoSpaceHasPalletLayers(cargoSpace));
}

function hasSingleLoadGroupAcross(workSpaces: WorkSpace[]): boolean {
  const ids = new Set<number>();
  for (const workSpace of workSpaces) {
    collectGroupIds(ids, workSpace?.outsideSpace?.groups);
    for (const cargoSpace of workSpace?.cargoSpaces ?? []) {
      if (cargoSpaceHasPalletLayers(cargoSpace)) {
        continue;
      }
      collectGroupIds(ids, listGroupsExcludingPalletLayers(cargoSpace));
    }
  }
  return ids.size === 1;
}

function palletTypeKey(workSpace: WorkSpace): string {
  const dimensions = workSpace?.cargoSpaces?.[0]?.dimensions;
  return `${dimensions?.length ?? 'none'}x${dimensions?.width ?? 'none'}x${dimensions?.height ?? 'none'}`;
}

function loadFitsSpace(group: Group, space: CargoSpace['dimensions']): boolean {
  const loadLength = group.dimensions?.length ?? 0;
  const loadWidth = group.dimensions?.width ?? 0;
  const loadHeight = group.dimensions?.height ?? 0;
  const spaceLength = space?.length ?? 0;
  const spaceWidth = space?.width ?? 0;
  const spaceHeight = space?.height ?? 0;
  const orientations: Array<[number, number, number]> = [[loadLength, loadWidth, loadHeight]];
  if (group.rotateOnBase || group.allowToRotate) {
    orientations.push([loadWidth, loadLength, loadHeight]);
  }
  if (group.allowToRotate || group.rotateFreely) {
    orientations.push(
      [loadLength, loadHeight, loadWidth],
      [loadWidth, loadHeight, loadLength],
      [loadHeight, loadLength, loadWidth],
      [loadHeight, loadWidth, loadLength],
    );
  }
  return orientations.some(
    ([length, width, height]) =>
      length <= spaceLength && width <= spaceWidth && height <= spaceHeight,
  );
}

function collectGroupIds(ids: Set<number>, groups: Group[] | undefined): void {
  for (const group of groups ?? []) {
    if ((group.quantity ?? 0) > 0 && group.id != null) {
      ids.add(group.id);
    }
  }
}

function groupQuantity(groups: Group[] | undefined): number {
  return (groups ?? []).reduce((sum, group) => sum + (group.quantity ?? 0), 0);
}
